import type { ConfiguratorController } from "../SugarConfiguratorController";
import { ConfiguratorDocumentHistory } from "../ConfiguratorDocumentHistory";

export type ConfiguratorHistoryState = {
  canUndo: boolean;
  canRedo: boolean;
  restoring: boolean;
};

export class ConfiguratorDocumentController {
  state: ConfiguratorHistoryState = {
    canUndo: false,
    canRedo: false,
    restoring: false,
  };

  private history?: ConfiguratorDocumentHistory;
  private controller?: ConfiguratorController;

  constructor(
    private readonly onStateChange: (state: ConfiguratorHistoryState) => void,
  ) {}

  bind(controller: ConfiguratorController): void {
    if (this.controller === controller) return;
    this.dispose();
    this.controller = controller;
    this.history = new ConfiguratorDocumentHistory(
      (document) => controller.loadRuntimeDocument(document),
      (state) => {
        this.state = {
          canUndo: state.canUndo,
          canRedo: state.canRedo,
          restoring: state.loading,
        };
        this.onStateChange(this.state);
      },
    );
    this.history.initialize(controller.captureRuntimeDocument());
    controller.setRuntimeCommitHandler(() => {
      this.history?.commit(controller.captureRuntimeDocument());
    });
    controller.engine?.addEventListener(
      "configurator-rotation-committed",
      this.onRotationCommitted,
    );
    window.addEventListener("keydown", this.onHistoryKeyDown);
  }

  undo(): Promise<void> {
    return this.history?.undo() ?? Promise.resolve();
  }

  redo(): Promise<void> {
    return this.history?.redo() ?? Promise.resolve();
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onHistoryKeyDown);
    this.controller?.engine?.removeEventListener(
      "configurator-rotation-committed",
      this.onRotationCommitted,
    );
    this.controller?.setRuntimeCommitHandler(() => {});
    this.controller = undefined;
    this.history = undefined;
  }

  private readonly onRotationCommitted = (): void => {
    // Selection controller also snapshots directly; keep as fallback for
    // any other dispatcher of configurator-rotation-committed.
    this.controller?.snapshot("Rotate module");
  };

  private readonly onHistoryKeyDown = (event: KeyboardEvent): void => {
    if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
    const key = event.key.toLowerCase();
    if (key !== "z" && key !== "y") return;
    const redo = key === "y" || (key === "z" && event.shiftKey);
    event.preventDefault();
    void (redo ? this.redo() : this.undo());
  };
}
